import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Admin Authentication Service
 * Handles all admin authentication operations and sets up global axios interceptors
 */
class AdminAuthService {
  constructor() {
    // Setup global axios interceptors for all admin services
    this.setupAxiosInterceptors();
  }

  /**
   * Setup axios interceptors to automatically attach tokens to requests
   */
  setupAxiosInterceptors() {
    // Configure axios defaults
    axios.defaults.baseURL = API_BASE_URL;
    axios.defaults.withCredentials = true;
    
    // Request interceptor - attach access token to all requests
    axios.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle token refresh on 401 errors
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            await this.refreshToken();
            const token = this.getAccessToken();
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed - clear tokens and redirect to login
            sessionStorage.removeItem('adminAccessToken');
            sessionStorage.removeItem('adminRefreshToken');
            sessionStorage.removeItem('adminUser');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Login admin with email and password
   */
  async login(email, password) {
    try {
      const response = await axios.post('/api/admin/auth/login', {
        email: email.toLowerCase().trim(),
        password,
      });

      const { admin, tokens } = response.data.data;

      // Store tokens and admin info in sessionStorage
      sessionStorage.setItem('adminAccessToken', tokens.accessToken);
      sessionStorage.setItem('adminRefreshToken', tokens.refreshToken);
      sessionStorage.setItem('adminUser', JSON.stringify(admin));

      return {
        admin,
        tokens,
      };
    } catch (error) {
      const errorData = error.response?.data;
      throw {
        message: errorData?.error?.message || 'Login failed',
        code: errorData?.error?.code || 'LOGIN_ERROR',
        attemptsLeft: errorData?.error?.attemptsLeft,
        isLocked: errorData?.error?.isLocked,
        lockedUntil: errorData?.error?.lockedUntil,
        response: error.response,
      };
    }
  }

  /**
   * Logout admin
   */
  async logout() {
    try {
      await axios.post('/api/admin/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API error:', error);
    } finally {
      // Always clear session storage
      sessionStorage.removeItem('adminAccessToken');
      sessionStorage.removeItem('adminRefreshToken');
      sessionStorage.removeItem('adminUser');
    }
  }

  /**
   * Get current admin info
   */
  async getCurrentAdmin() {
    try {
      const response = await axios.get('/api/admin/auth/me');
      const admin = response.data.data.admin;
      
      // Update stored admin info
      sessionStorage.setItem('adminUser', JSON.stringify(admin));
      
      return admin;
    } catch (error) {
      throw {
        message: error.response?.data?.error?.message || 'Failed to get admin info',
        code: error.response?.data?.error?.code || 'FETCH_ERROR',
        response: error.response,
      };
    }
  }

  /**
   * Check login status for an email
   */
  async checkLoginStatus(email) {
    try {
      const response = await axios.post(
        '/api/admin/auth/check-status',
        { email: email.toLowerCase().trim() },
        { withCredentials: true }
      );
      
      return response.data.data;
    } catch (error) {
      throw {
        message: error.response?.data?.error?.message || 'Failed to check status',
        code: error.response?.data?.error?.code || 'STATUS_ERROR',
        response: error.response,
      };
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    try {
      const refreshToken = sessionStorage.getItem('adminRefreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(
        '/api/admin/auth/refresh',
        { refreshToken },
        { withCredentials: true }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
      
      sessionStorage.setItem('adminAccessToken', accessToken);
      sessionStorage.setItem('adminRefreshToken', newRefreshToken);

      return { accessToken, refreshToken: newRefreshToken };
    } catch (error) {
      // Clear tokens on refresh failure
      sessionStorage.removeItem('adminAccessToken');
      sessionStorage.removeItem('adminRefreshToken');
      sessionStorage.removeItem('adminUser');
      
      throw {
        message: error.response?.data?.error?.message || 'Token refresh failed',
        code: error.response?.data?.error?.code || 'REFRESH_ERROR',
        response: error.response,
      };
    }
  }

  /**
   * Check if admin is authenticated
   */
  isAuthenticated() {
    const token = sessionStorage.getItem('adminAccessToken');
    const user = sessionStorage.getItem('adminUser');
    return !!(token && user);
  }

  /**
   * Get stored admin user
   */
  getStoredAdmin() {
    try {
      const adminData = sessionStorage.getItem('adminUser');
      return adminData ? JSON.parse(adminData) : null;
    } catch (error) {
      console.error('Error parsing stored admin data:', error);
      return null;
    }
  }

  /**
   * Get stored access token
   */
  getAccessToken() {
    return sessionStorage.getItem('adminAccessToken');
  }

  /**
   * Check service health
   */
  async checkHealth() {
    try {
      const response = await axios.get('/api/admin/auth/health');
      return response.data;
    } catch (error) {
      throw {
        message: 'Admin service unavailable',
        code: 'SERVICE_UNAVAILABLE',
        response: error.response,
      };
    }
  }

  /**
   * Create a new admin account (SUPERADMIN only)
   */
  async createAdmin(adminData) {
    try {
      const response = await axios.post(
        '/api/admin/management/create-admin',
        adminData,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('adminAccessToken')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      return response.data.data;
    } catch (error) {
      throw {
        message: error.response?.data?.error?.message || 'Failed to create admin',
        code: error.response?.data?.error?.code || 'CREATE_ADMIN_ERROR',
        field: error.response?.data?.error?.field,
        response: error.response,
      };
    }
  }

  /**
   * Change admin password
   */
  async changePassword(passwordData) {
    try {
      const response = await axios.put(
        '/api/admin/management/change-password',
        passwordData,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('adminAccessToken')}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        }
      );
      
      // If password change requires reauth, clear tokens
      if (response.data.data?.requiresReauth) {
        sessionStorage.removeItem('adminAccessToken');
        sessionStorage.removeItem('adminRefreshToken');
        sessionStorage.removeItem('adminUser');
      }
      
      return response.data;
    } catch (error) {
      throw {
        message: error.response?.data?.error?.message || 'Failed to change password',
        code: error.response?.data?.error?.code || 'PASSWORD_CHANGE_ERROR',
        field: error.response?.data?.error?.field,
        response: error.response,
      };
    }
  }

  /**
   * Get admin statistics (SUPERADMIN only)
   */
  async getAdminStats() {
    try {
      const response = await axios.get(
        '/api/admin/management/stats',
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('adminAccessToken')}`,
          },
          withCredentials: true,
        }
      );
      return response.data.data;
    } catch (error) {
      throw {
        message: error.response?.data?.error?.message || 'Failed to fetch admin stats',
        code: error.response?.data?.error?.code || 'STATS_ERROR',
        response: error.response,
      };
    }
  }

  /**
   * Check admin management service health
   */
  async checkManagementHealth() {
    try {
      const response = await axios.get('/api/admin/management/health');
      return response.data;
    } catch (error) {
      throw {
        message: 'Admin management service unavailable',
        code: 'SERVICE_UNAVAILABLE',
        response: error.response,
      };
    }
  }
}

// Export singleton instance
export default new AdminAuthService();
